import React from "react"

import { useContext, useState, useEffect } from "react"
import intl from "react-intl-universal"
import { useLocation } from "react-router-dom"

import styles from "./index.module.scss"
import DashboardIcon from "../../../assets_v2/icons/DashboardIcon"
import HomeIcon from "../../../assets_v2/icons/HomeIcon"
import MedalIcon from "../../../assets_v2/icons/MedalIcon"
import CheckedIcon from "../../../assets_v2/icons/CheckedIcon"
import SignalIcon from "../../../assets_v2/icons/SignalIcon"
import DocumentIcon from "../../../assets_v2/icons/DocumentIcon"
import RcmpLink from "../../atoms/Link"
import { fnCanAtLeast } from "../../../utils/permissions"
import UserIcon from "../../../assets_v2/icons/UserIcon"
import TeamIcon from "../../../assets_v2/icons/TeamIcon"
import TrashIcon from "../../../assets_v2/icons/TrashIcon"
import SideBarContext from "../../../contexts/SideBar"
import CIQIcon from "../../../assets_v2/icons/CIQIcon"
import { BrandImage } from "../../../contexts/Appearance"
import logo from '../../../assets/img/logos/logo-56x62.svg';
import logoOpen from '../../../assets/img/logos/logo-text-inline-140x24.svg';
import IndicatorsIcon from "../../../assets_v2/icons/IndicatorsIcon"
import AuthContext from "../../../contexts/Auth"
import AlertHexagonIcon from "../../../assets_v2/icons/AlertHexagonIcon"
import NonConformitiesIcon from "../../../assets_v2/icons/NonConformitiesIcon"
import SearchIcon from "../../../assets_v2/icons/SearchIcon"

const CIQ_PATH = "/ciq"

function SideBarItemContent(props) {
  const { item, bShowTexts } = props
  return (
    <>
      <div className={styles.icon}>{item.icon}</div>
      <div className={bShowTexts ? `${styles.linkText}` : `${styles.hiddenText}`}>
        <span>{item.name}</span>
      </div>
    </>
  )
}

function SideBarItem(props) {
  const { item, bShowTexts, ...oRest } = props

  if (!item?.bShow) return null

  const pathName = window.location.pathname

  let cActiveClass = ""
  const itemPath = item.path.startsWith("./") ? item.path.slice(1) : item.path
  if (
    pathName === itemPath || pathName.startsWith(itemPath + "/")
  ) {
    cActiveClass = styles.activeLink
  }

  return (
    <RcmpLink to={item.path} className={`${styles.link} ${cActiveClass}`} {...oRest}>
      <SideBarItemContent item={item} bShowTexts={bShowTexts} />
    </RcmpLink>
  )
}

export default function SideBar({ children }) {
  const {
    bSideBarIsOpen,
    fnSetBSideBarIsOpen,
    bSideBarOnAfterOpen,
    fnSetBSideBarOnAfterOpen,
    bSideBarOnAfterClose,
    fnSetBSideBarOnAfterClose,
  } = useContext(SideBarContext)

  const { oUser } = useContext(AuthContext)

  const [bHovering, setBHovering] = useState(false)

  const location = useLocation()

  const bHasIndicatorModule = oUser?.data?.bHasIndicatorModule || false
  const bHasRiskAndNonConformitesModule = oUser?.data?.bHasRiskAndNonConformitesModule || false
  const bIsDocumentsV2 = oUser?.data?.bIsDocumentsV2 || false

  useEffect(() => {
    fnSetBSideBarIsOpen(false)
  }, [location])

  useEffect(() => {
    let timer
    if (bHovering) {
      timer = setTimeout(() => {
        fnSetBSideBarIsOpen(true)
      }, 250)
    } else {
      fnSetBSideBarIsOpen(false)
    }

    return () => clearTimeout(timer)
  }, [bHovering])

  const handleOpenSideBar = () => {
    setBHovering(true)
    fnSetBSideBarOnAfterClose(false)
  }

  const handlCloseSideBar = () => {
    setBHovering(false)
  }

  const fnOnAfterOpen = () => {
    setTimeout(() => {
      fnSetBSideBarOnAfterOpen(bSideBarIsOpen)
    }, 200)

    if (!bSideBarIsOpen) {
      fnSetBSideBarOnAfterClose(true)
    }
  }

  const menuItem = [
    {
      path: "./",
      name: intl.get("home"),
      icon: <HomeIcon />,
      bShow: true,
    },
    {
      path: "/quality-panel",
      name: intl.get("sidemenu.quality-panel"),
      icon: <MedalIcon />,
      bShow: fnCanAtLeast("quality-panel", "quality-panel"),
    },
    {
      path: "/audit-management",
      name: intl.get("sidemenu.audit-management"),
      icon: <CheckedIcon />,
      bShow: fnCanAtLeast("audit-management", "audit-management"),
    },
    {
      path: "./dashboard",
      name: intl.get("sidemenu.dashboard"),
      icon: <DashboardIcon />,
      bShow: fnCanAtLeast("operational-panel", "operational-panel"),
    },
    {
      path: "/folder_old",
      name: intl.get("sidemenu.folder"),
      icon: <DocumentIcon />,
      bShow: !bIsDocumentsV2 && fnCanAtLeast("documents", "documents"),
    },
    {
      // Usa a rota neutra para o gateway entregar o frontend correto do Documents V2.
      path: "./documents",
      name: intl.get("sidemenu.folder"),
      icon: <DocumentIcon />,
      bShow: bIsDocumentsV2 && fnCanAtLeast("documents", "documents"),
    },
    {
      path: "./ciq",
      name: "CIQ",
      icon: <CIQIcon />,
      bShow: fnCanAtLeast("access-ciq", "internal-quality-control"),
    },
    {
      path: "./reports",
      name: intl.get("sidemenu.reports"),
      icon: <SignalIcon />,
      bShow: true,
    },
    {
      // Recarrega a rota para que o gateway entregue o módulo no frontend novo.
      path: "./monitoring",
      name: intl.get("sidemenu.monitoring"),
      icon: <SearchIcon />,
      bShow: fnCanAtLeast("monitoring-view"),
    },
    {
      path: "./indicators",
      name: intl.get("sidemenu.indicators"),
      icon: <IndicatorsIcon />,
      bShow: bHasIndicatorModule && fnCanAtLeast("indicator", "operational-panel"),
    },
    {
      path: "./risk-control",
      name: intl.get("sidemenu.risk-control"),
      icon: <AlertHexagonIcon />,
      bShow: bHasRiskAndNonConformitesModule && fnCanAtLeast(["access-risk-management", "edit-risk-management"], "operational-panel"),
    },
    {
      path: "./non-conformities",
      name: intl.get("sidemenu.non-conformities"),
      icon: <NonConformitiesIcon />,
      bShow: bHasRiskAndNonConformitesModule && fnCanAtLeast(["access-non-conformities", "edit-non-conformities"], "operational-panel"),
    },
  ]

  const menuPersonal = [
    {
      path: "/user",
      name: intl.get("sidemenu.users"),
      icon: <UserIcon />,
      bShow: fnCanAtLeast(["create-edit-user", "admin"]),
    },
    {
      path: "/team",
      name: intl.get("sidemenu.team"),
      icon: <TeamIcon />,
      bShow: fnCanAtLeast(["create-edit-team", "admin"]),
    },
    {
      path: "/trash",
      name: intl.get("sidemenu.trash"),
      icon: <TrashIcon height={18} width={18} />,
      bShow: fnCanAtLeast("admin"),
    },
  ]

  const bShowTexts = bSideBarIsOpen && bSideBarOnAfterOpen
  const cMaskClass = `${styles.sidebarMask} ${bSideBarIsOpen && !bSideBarOnAfterClose ? `${styles.isOpen}` : ""} ${!bSideBarIsOpen && !bSideBarOnAfterClose && bSideBarOnAfterOpen ? `${styles.isClose}` : ""} ${bSideBarOnAfterClose ? `${styles.isClosed}` : ""}`

  return (
    <div>
      <div className={cMaskClass} onClick={() => fnSetBSideBarIsOpen(false)}></div>
      <div className={styles.sidebarContainer}>
        <div
          className={`${styles.sidebarAnchor} ${bSideBarIsOpen ? `${styles.isOpenAnchor}` : ""}`}
          onTransitionEnd={fnOnAfterOpen}
          onMouseLeave={handlCloseSideBar}
        >
          <div
            className={`${styles.sidebar} ${bSideBarIsOpen ? `${styles.isOpen}` : ""}`}
          >
            <div className={styles.topSection}>
              <RcmpLink
                className={
                  bSideBarIsOpen
                    ? `${styles.displayInlineBlock}`
                    : `${styles.displayNone}`
                }
                to="./"
              >
                {!bSideBarIsOpen ? (
                  <BrandImage
                    logo={logo}
                    cFileName={"logo-56x62.svg"}
                    className={styles.desktopLogo}
                    bHasTitleAlt
                  />
                ) : (
                  <BrandImage
                    logo={logoOpen}
                    cFileName="logo-text-inline-140x24.svg"
                    className={styles.desktopLogo}
                    bHasTitleAlt
                  />
                )}
              </RcmpLink>
            </div>

            <div className={`${styles.items} ${styles.middleSection}`}>
              {menuItem.map((item, index) => (
                <SideBarItem
                  key={`sidebar-item-${index}`}
                  item={item}
                  bShowTexts={bShowTexts}
                  onMouseOver={handleOpenSideBar}
                  onClick={handlCloseSideBar}
                />
              ))}
            </div>
            <div className={styles.items}>
              {menuPersonal.map((item, index) => (
                <SideBarItem
                  key={`sidebar-personal-item-${index}`}
                  item={item}
                  bShowTexts={bShowTexts}
                  onMouseOver={handleOpenSideBar}
                />
              ))}
            </div>
          </div>
        </div>
        <main id="mainContent" className={styles.pageContent}>
          {children}
        </main>
      </div>
    </div>
  )
}
